import { useState } from 'react';
import { 
  Home, 
  DollarSign, 
  BookOpen, 
  Users, 
  BarChart3, 
  HardDrive, 
  Settings, 
  UserPlus, 
  Palette, 
  Package, 
  Shield, 
  Globe, 
  Award, 
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';

interface DashboardMobileDropdownProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isPro: boolean;
  hasAccess?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  proOnly?: boolean;
  subItems?: NavItem[];
}

export default function DashboardMobileDropdown({ 
  activeSection, 
  onSectionChange, 
  isPro,
  hasAccess = true
}: DashboardMobileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const navItems: NavItem[] = [
    {
      id: 'overview',
      label: 'ภาพรวม',
      icon: Home,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
    },
    {
      id: 'revenue',
      label: 'รายได้',
      icon: DollarSign,
    },
    {
      id: 'courses',
      label: 'คอร์ส',
      icon: BookOpen,
    },
    {
      id: 'students',
      label: 'นักเรียน',
      icon: Users,
    },
    {
      id: 'moderator-management',
      label: 'จัดการผู้ดูแล (Moderator)',
      icon: Shield,
      proOnly: true,
    },
    {
      id: 'storage',
      label: 'พื้นที่จัดเก็บ',
      icon: HardDrive,
      proOnly: true,
    },
    {
      id: 'approve-students',
      label: 'อนุมัตินักเรียน',
      icon: BookOpen,
    },
    {
      id: 'student-reports',
      label: 'รายงานนักเรียน',
      icon: Users,
    },
    {
      id: 'discounts',
      label: 'จัดการส่วนลด',
      icon: BarChart3,
    },
    {
      id: 'team-management',
      label: 'จัดการทีม',
      icon: UserPlus,
      proOnly: true,
    },
    {
      id: 'white-label',
      label: 'White Label',
      icon: Palette,
      proOnly: true,
    },
    {
      id: 'storage-addons',
      label: 'Add-ons (นักเรียน+พื้นที่)',
      icon: Package,
      proOnly: true,
    },
    {
      id: 'custom-domain',
      label: 'Custom Domain',
      icon: Globe,
      proOnly: true,
    },
    {
      id: 'sla-support',
      label: 'SLA Support',
      icon: Shield,
      proOnly: true,
    },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (item.proOnly && !isPro) return false;
    return true;
  }).map(item => {
    if (item.subItems) {
      return {
        ...item,
        subItems: item.subItems.filter(subItem => {
          if (subItem.proOnly && !isPro) return false;
          return true;
        })
      };
    }
    return item;
  });

  const getCurrentLabel = () => {
    for (const item of filteredNavItems) {
      if (item.id === activeSection) return item.label;
      if (item.subItems) {
        const subItem = item.subItems.find(sub => sub.id === activeSection);
        if (subItem) return subItem.label;
      }
    }
    return 'ภาพรวม';
  };

  const getCurrentIcon = () => {
    for (const item of filteredNavItems) {
      if (item.id === activeSection) return item.icon;
      if (item.subItems) {
        const subItem = item.subItems.find(sub => sub.id === activeSection);
        if (subItem) return subItem.icon;
      }
    }
    return Home;
  };

  const CurrentIcon = getCurrentIcon();

  return (
    <div className="lg:hidden">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm"
      >
        <div className="flex items-center gap-3">
          <CurrentIcon className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-gray-900">{getCurrentLabel()}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="max-h-96 overflow-y-auto">
            {filteredNavItems.map(item => {
              const isActive = activeSection === item.id;
              const ItemIcon = item.icon;
              const isDisabled = !hasAccess && item.id !== 'overview';
              
              return (
                <div key={item.id}>
                  <button
                    onClick={() => {
                      if (isDisabled) return;
                      if (item.id === 'salepage') {
                        // Open external Salepage URL in new window
                        window.open('https://saledi.web.app/salepage/dashboard', '_blank');
                        setIsOpen(false);
                      } else if (!item.subItems) {
                        onSectionChange(item.id);
                        setIsOpen(false);
                      }
                    }}
                    disabled={isDisabled}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                      isDisabled
                        ? 'cursor-not-allowed opacity-50 bg-gray-50 text-gray-400'
                        : isActive
                        ? item.id === 'salepage'
                          ? 'bg-gradient-to-r from-emerald-50 to-cyan-50 text-emerald-700'
                          : 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <ItemIcon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {item.badge && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.id === 'salepage'
                          ? 'bg-gradient-to-r from-emerald-100 to-cyan-100 text-emerald-700 font-semibold'
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                  
                  {item.subItems && item.subItems.map(subItem => {
                    const isSubActive = activeSection === subItem.id;
                    const SubIcon = subItem.icon;
                    const isSubDisabled = !hasAccess;
                    
                    return (
                      <button
                        key={subItem.id}
                        onClick={() => {
                          if (isSubDisabled) return;
                          onSectionChange(subItem.id);
                          setIsOpen(false);
                        }}
                        disabled={isSubDisabled}
                        className={`w-full flex items-center justify-between pl-12 pr-4 py-2.5 transition-colors ${
                          isSubDisabled
                            ? 'cursor-not-allowed opacity-50 bg-gray-50 text-gray-400'
                            : isSubActive
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <SubIcon className="w-4 h-4" />
                          <span className="text-sm font-medium">{subItem.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
